import { TextField } from "@package/subframe/components/TextField";
import { type ComponentProps, useState } from "react";

import { signedDecimalInputProps } from "@/lib/is-ios";
import { parseDecimalInput, sanitizeDecimalInput } from "@/lib/number-input";

type NumberInputProps = Omit<
  ComponentProps<typeof TextField.Input>,
  "type" | "value" | "onChange"
> & {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
};

/**
 * Decimal number input for use inside a `<TextField>`.
 *
 * Uses `type="text"` (not `type="number"`) so the iOS keyboard exposes a minus
 * key, and buffers the raw text locally so a leading `-` or trailing `.`
 * survives while the user is still typing. `onValueChange` fires with the parsed
 * number, or `null` while the value is empty/incomplete.
 */
export function NumberInput({
  value,
  onValueChange,
  ...props
}: NumberInputProps) {
  const [text, setText] = useState(() =>
    typeof value === "number" ? String(value) : "",
  );
  const [lastValue, setLastValue] = useState(value);

  // Resync when the value changes externally without clobbering an in-progress
  // entry like "-" or "-0.".
  if (value !== lastValue) {
    setLastValue(value);
    if (value !== parseDecimalInput(text)) {
      setText(typeof value === "number" ? String(value) : "");
    }
  }

  return (
    <TextField.Input
      {...props}
      {...signedDecimalInputProps()}
      type="text"
      pattern="-?[0-9]*\.?[0-9]*"
      value={text}
      onChange={(e) => {
        const clean = sanitizeDecimalInput(e.target.value);
        setText(clean);
        onValueChange(parseDecimalInput(clean));
      }}
    />
  );
}
